import {
  featureCollection as toFeatureCollection,
  multiLineString as toMultiLineString,
  point as toPoint,
  length
} from "@turf/turf";
import { flatMap, sumBy, every, merge, last } from 'lodash';
import {
  filterFeatureCollection,
  convertToLineStringFeatures,
  convertToMultiLineString
} from "../../../utilities/geojson_adapters";
import generateId from "../../../utilities/random_id_generator";
import {
  lineToLinePaint,
  lineToLineLayout
} from "utilities/mapbox_style_adapters";
import { applyDeviationsToLineStyle } from "../../../utilities/mapbox_style_adapters";


export default class AnimatableLine {
  defaultStyleOptions = {
    'layout': {
      'line-cap': 'round',
      'line-join': 'round'
    },
    'paint': {
      'line-color': '#ed6498',
      'line-width': 5,
      'line-opacity': 0.8
    }
  };

  constructor({ featureCollection, map, lineStyle, time = 3500, refreshRate = 50.0, detailsOnHover = null }) {
    if (featureCollection.type != "FeatureCollection") {
      throw "Must pass FeatureCollection";
    }
    this.id = generateId();
    this.map = map;
    this.detailsOnHover = detailsOnHover;
    this.baseTime = time;
    this.baseRefreshRate = refreshRate;

    this.lineStyle = lineStyle || {};

    // TODO: we should combine those line strings that have style properties
    // that will display identically. This will fix blotchy dashed/dotted lines.
    const lineFeatures = flatMap(
      filterFeatureCollection({
        featureCollection,
        types: ["LineString", "MultiLineString"]
      })
        .features
        .map((feature) => {
          if (feature.geometry.type == "LineString") return feature;

          return convertToLineStringFeatures({ multiLineStringFeature: feature });
        })
    );

    const totalDistance = sumBy(lineFeatures, (feature) => {
      const dist = length(feature.geometry);
      feature.properties = {
        ...feature.properties,
        ...{ distance: dist }
      };
      return dist;
    });

    // Convert every LineString – each of which should have contextually unique
    // properties from its neighbors – into a MultiLineString so that each slice of the
    // MultiLineString corresponds to one tick in animation.
    //
    const distancePerAnimationTick = totalDistance / (time / refreshRate);
    let distanceRemainder = 0;
    const multiLineStringFeatures = lineFeatures.map((lineStringFeature) => {
      const multiLineStringFeature = convertToMultiLineString({
        lineStringFeature,
        distancePerSegment: distancePerAnimationTick,
        initialSegmentOffset: distanceRemainder
      });
      multiLineStringFeature.properties = lineStringFeature.properties;

      const resolvedDistance = distanceRemainder + multiLineStringFeature.properties.distance;
      distanceRemainder = resolvedDistance % distancePerAnimationTick;

      return multiLineStringFeature;
    });
    this.distancePerAnimationTick = distancePerAnimationTick;

    let cursorIndexingMax = -1;
    multiLineStringFeatures.forEach((multiLineStringFeature, index) => {
      cursorIndexingMax += multiLineStringFeature.geometry.coordinates.length;
      multiLineStringFeature.properties.cursorIndexMax = cursorIndexingMax;
      multiLineStringFeature.properties.index = index;

      this.map.addSource(this.sourceId(index), {
        'type': 'geojson',
        'data': toFeatureCollection([multiLineStringFeature])
      });
    });
    this.cursor = this.cursorMax = cursorIndexingMax;
    this.featureCollection = toFeatureCollection(multiLineStringFeatures);

    if (this.detailsOnHover) {
      this.featureCollection.features.forEach((feature) => {
        const layerId = this.layerId(feature.properties.index);

        this.map.on('mouseenter', layerId, (event) => {
          if (this.details == "off") return;

          this.map.getCanvas().style.cursor = 'pointer';
          this.map.setPaintProperty(layerId, 'line-gap-width', 1);

          this.detailsOnHover({ event, feature, layerId });
        });

        this.map.on('mouseleave', layerId, (event) => {
          if (this.details == "off") return;

          this.map.getCanvas().style.cursor = '';
          this.map.setPaintProperty(layerId, 'line-gap-width', 0);

          this.detailsOnHover({ event, feature, layerId });
        });
      });
    }

    this._details = 'off';
  }

  get details() {
    return this._details;
  }

  set details(value) {
    if (!['off', 'on'].includes(value)) throw "Argument must be on or off";

    this._details = value;

    this.featureCollection.features.forEach((feature) => {
      const { index } = feature.properties;
      const layerId = this.layerId(index);

      if (this.map.getLayer(layerId)) {
        this.hideFeature(feature);
        this.showFeature(feature);
      }
    });
  }

  isFullyShowing() {
    return every(this.featureCollection.features, (feature) => {
      return this.map.getLayer(this.layerId(feature.properties.index));
    });
  }

  show() {
    this.featureCollection.features.forEach((feature) => {
      this.showFeature(feature);
    });
  }

  showFeature(feature) {
    const { index } = feature.properties;
    const layerId = this.layerId(index);

    if (this.map.getLayer(layerId)) return;

    const style = this._featureStyle(feature);
    this.map.addLayer({
      'id': layerId,
      'type': 'line',
      'source': this.sourceId(index),
      ...style
    });
  }

  hide() {
    this.featureCollection.features.forEach((feature) => {
      const { index } = feature.properties;
      const layerId = this.layerId(index);

      if (!this.map.getLayer(layerId)) return;

      this.map.removeLayer(layerId);
    });
  }

  hideFeature(feature) {
    const { index } = feature.properties;
    const layerId = this.layerId(index);

    if (!this.map.getLayer(layerId)) return;

    this.map.removeLayer(layerId);
  }

  remove() {
    this.hide();

    this.featureCollection.features.forEach((feature) => {
      const index = feature.properties.index;

      if (!this.map.getSource(this.sourceId(index))) return;

      this.map.removeSource(this.sourceId(index));
    });
  }

  startCoordinates() {
    return this.featureCollection.features[0].geometry.coordinates[0][0];
  }

  finishCoordinates() {
    const featuresLength = this.featureCollection.features.length;
    const feature = this.featureCollection.features[featuresLength - 1];
    const lineStringsCount = feature.geometry.coordinates.length;
    const coordinateCount = feature.geometry.coordinates[lineStringsCount - 1].length;

    return feature.geometry.coordinates[lineStringsCount - 1][coordinateCount - 1];
  }

  layerId(index) {
    return `animatable-line-layer-${this.id}-${index}`;
  }

  sourceId(index) {
    return `animatable-line-source-${this.id}-${index}`;
  }

  animationReset() {
    this.cursor = -1;

    this.hide();
  }

  nextAnimationTick({ previousExcessDistance }) {
    if (this.animationComplete()) return false;

    const prevCursor = this.cursor;
    let nextCursor = this.cursor;
    let distance = previousExcessDistance;
    do {
      nextCursor += 1;
      const { feature, localCursor } = this._featureAndLocalCursorAt(nextCursor);
      const lineStringGeometry = {
        type: "LineString",
        coordinates: feature.geometry.coordinates[localCursor]
      };
      distance += length(lineStringGeometry);
    } while(distance < this.distancePerAnimationTick && (nextCursor + 1) <= this.cursorMax);
    this.cursor = nextCursor;

    const lastCoordinate = this._setMapSourceToAnimationCursorChange({ was: prevCursor });
    const excessDistance = distance - this.distancePerAnimationTick;
    if (lastCoordinate) {
      return { terminus: toPoint(lastCoordinate), excessDistance };
    } else {
      return { terminus: null, excessDistance };
    }
  }

  animationComplete() {
    return this.cursor == this.cursorMax;
  }

  _featureStyle(feature) {
    let lineStyle = this.lineStyle;
    if (this._details == 'on') {
      lineStyle = applyDeviationsToLineStyle(
        this.lineStyle,
        feature.properties
      );
    }

    const layout = lineToLineLayout(lineStyle);
    const paint = lineToLinePaint(lineStyle);
    const featureStyle = {};
    merge(
      featureStyle,
      this.defaultStyleOptions,
      layout,
      paint
    );
    return featureStyle;
  }

  _featureAndLocalCursorAt(cursorValue) {
    const feature = this._featureAtCursorValue(cursorValue);

    if (!feature) {
      return { feature, localCursor: null };
    }

    const localCursorIndexMax = feature.properties.cursorIndexMax;
    const localCoordsLen = feature.geometry.coordinates.length;
    const localCursor = cursorValue - (localCursorIndexMax - localCoordsLen + 1);

    return { localCursor, feature };
  }

  _featureAtCursorValue(cursorValue) {
    const feature = this.featureCollection.features.find((feature) => {
      return cursorValue <= feature.properties.cursorIndexMax;
    });

    return feature;
  }

  _setMapSourceToAnimationCursorChange({ was }) {
    let cursor = was + 1;
    let lastRevisedFeature = null;

    while(cursor <= this.cursor) {
      const { feature, localCursor } = this._featureAndLocalCursorAt(cursor);

      lastRevisedFeature = toMultiLineString(
        feature.geometry.coordinates.slice(0, localCursor + 1)
      );

      this.map
        .getSource(this.sourceId(feature.properties.index))
        .setData(toFeatureCollection([lastRevisedFeature]));
      this.showFeature(feature);

      cursor++;
    }

    const lastCoordinate = last(last(lastRevisedFeature.geometry.coordinates));
    return lastCoordinate;
  }
}
